import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Building2, ClipboardList, History, Loader2, RefreshCw } from 'lucide-react';
import AppPageHeader from '../shell/AppPageHeader';
import { useFirmMainHeaderSlot } from './FirmMainHeaderContext';
import { useFirmCompanies } from './FirmCompaniesContext';
import {
  RunSnapshotSlideOver,
  runHistoryRowToSnapshotSlideSubject,
  type RunSnapshotSlideSubject,
} from '../client/runHistory';
import {
  FirmAnalysisPersistedLockMark,
  FirmLiveAnalysisExpandChevron,
  FirmLiveAnalysisRunHistoryPanel,
  FirmLiveAnalysisWorkflowStatusBadge,
} from './FirmLiveAnalysisTableParts';
import {
  fetchCompanyAnalyses,
  type AnalysisRunRow,
  type CompanyLiveAnalysisRow,
} from '../../services/FirmCompaniesApiClient';
import { shellMainPaddingX } from '../../utils/shellLayoutClasses';
import { clientDataPanelClass } from '../../utils/clientChromeClasses';
import { mapWithConcurrency } from '../../utils/mapWithConcurrency';
import { formatFirmAnalysisUpdatedSubtitle } from './firmLiveAnalysisTableUtils';

const COMPANY_ANALYSES_FETCH_CONCURRENCY = 4;

function formatWorkspaceDate(iso: string): string {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type EnrichedLive = CompanyLiveAnalysisRow & { companyId: string; companyName: string };
type EnrichedLegacyRun = AnalysisRunRow & { companyId: string; companyName: string };

export default function Dashboard() {
  const navigate = useNavigate();
  const { companies: rows, loading: companiesLoading, error, refreshCompanies, lawFirmId } = useFirmCompanies();

  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState(null as string | null);
  const [partialWorkspaceFailure, setPartialWorkspaceFailure] = useState(false);
  const [liveAnalyses, setLiveAnalyses] = useState([] as EnrichedLive[]);
  const [legacyRuns, setLegacyRuns] = useState([] as EnrichedLegacyRun[]);
  const [legacyRunCount, setLegacyRunCount] = useState(0);
  const [legacyOverTotal, setLegacyOverTotal] = useState(0);
  const [expandedLiveAnalysisRowIds, setExpandedLiveAnalysisRowIds] = useState([] as string[]);
  const [runSnapshotSubject, setRunSnapshotSubject] = useState(null as RunSnapshotSlideSubject | null);

  const loadWorkspaceFromApi = useCallback(async () => {
    if (rows.length === 0) {
      setLiveAnalyses([]);
      setLegacyRuns([]);
      setLegacyRunCount(0);
      setLegacyOverTotal(0);
      setWorkspaceError(null);
      setPartialWorkspaceFailure(false);
      setWorkspaceLoading(false);
      return;
    }

    setWorkspaceLoading(true);
    setWorkspaceError(null);
    setPartialWorkspaceFailure(false);

    const results = await Promise.allSettled(
      await mapWithConcurrency(rows, COMPANY_ANALYSES_FETCH_CONCURRENCY, (c) =>
        fetchCompanyAnalyses(lawFirmId, c.id).then((payload) => ({
          companyId: c.id,
          companyName: c.companyName,
          payload,
        }))
      )
    );

    const enriched: EnrichedLive[] = [];
    const legacyEnriched: EnrichedLegacyRun[] = [];
    let legacyRuns = 0;
    let legacyOver = 0;
    let anyFailed = false;

    for (const r of results) {
      if (r.status === 'rejected') {
        anyFailed = true;
        continue;
      }
      const { companyId, companyName, payload } = r.value;
      const runs: AnalysisRunRow[] = payload.analysisRuns ?? [];
      legacyRuns += runs.length;
      for (const run of runs) {
        legacyOver += run.overCount ?? 0;
        legacyEnriched.push({ ...run, companyId, companyName });
      }
      for (const a of payload.analyses ?? []) {
        enriched.push({ ...a, companyId, companyName });
      }
    }

    enriched.sort((a, b) => {
      const ta = new Date(a.updatedAt).getTime();
      const tb = new Date(b.updatedAt).getTime();
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return tb - ta;
    });

    setLiveAnalyses(enriched);
    setLegacyRuns(legacyEnriched);
    setLegacyRunCount(legacyRuns);
    setLegacyOverTotal(legacyOver);
    setPartialWorkspaceFailure(anyFailed);
    if (results.every((x) => x.status === 'rejected')) {
      setWorkspaceError('Could not load workspace analyses for any company.');
    } else {
      setWorkspaceError(null);
    }
    setWorkspaceLoading(false);
  }, [lawFirmId, rows]);

  useEffect(() => {
    if (companiesLoading) return;
    loadWorkspaceFromApi();
  }, [companiesLoading, loadWorkspaceFromApi]);

  const summaryBadges = useMemo(
    () => [
      {
        label: 'Companies',
        value: String(rows.length),
        loading: false,
        tone: 'slate',
        helper: 'Active in this firm workspace',
      },
      {
        label: 'Live workspace analyses',
        value: workspaceLoading && rows.length > 0 ? '…' : String(liveAnalyses.length),
        loading: workspaceLoading && rows.length > 0,
        tone: 'emerald',
        helper: 'Current client workspace rows',
      },
      {
        label: 'Legacy analysis runs',
        value: workspaceLoading && rows.length > 0 ? '…' : String(legacyRunCount),
        loading: workspaceLoading && rows.length > 0,
        tone: 'indigo',
        helper: 'Historical batch runs',
      },
      {
        label: 'Legacy over-threshold individuals',
        value: workspaceLoading && rows.length > 0 ? '…' : String(legacyOverTotal),
        loading: workspaceLoading && rows.length > 0,
        tone: 'amber',
        helper: 'Flagged by legacy runs',
      },
    ],
    [rows.length, liveAnalyses.length, legacyRunCount, legacyOverTotal, workspaceLoading]
  );

  const firmHeader = useMemo(() => <AppPageHeader variant="firmShell" title="Dashboard" />, []);
  useFirmMainHeaderSlot(firmHeader);

  const showCompaniesTable = !companiesLoading && !error && rows.length > 0;
  const workspaceReady = !workspaceLoading && rows.length > 0 && !workspaceError;

  const legacyRunsByCompanyAndAnalysisKey = useMemo(() => {
    const map = new Map<string, AnalysisRunRow[]>();
    for (const run of legacyRuns) {
      const analysisKey = String(run.analysisName ?? '')
        .trim()
        .toLowerCase();
      if (!analysisKey) continue;
      const key = `${run.companyId}|${analysisKey}`;
      const current = map.get(key);
      if (current) current.push(run);
      else map.set(key, [run]);
    }

    // Sort newest first per key.
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => {
        const ta = new Date(a.runDate ?? '').getTime();
        const tb = new Date(b.runDate ?? '').getTime();
        if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
        if (Number.isNaN(ta)) return 1;
        if (Number.isNaN(tb)) return -1;
        return tb - ta;
      });
      map.set(k, list);
    }

    return map;
  }, [legacyRuns]);

  const getLegacyRunsForAnalysisKey = (companyId: string, analysisKey: string): AnalysisRunRow[] => {
    if (!analysisKey) return [];
    const exact = legacyRunsByCompanyAndAnalysisKey.get(`${companyId}|${analysisKey}`);
    if (exact && exact.length > 0) return exact;

    let best: AnalysisRunRow[] | undefined;
    let bestKeyLen = -1;
    const prefix = `${companyId}|`;
    for (const [k, v] of legacyRunsByCompanyAndAnalysisKey.entries()) {
      if (!k.startsWith(prefix) || v.length === 0) continue;
      const keyAnalysis = k.slice(prefix.length);
      if (keyAnalysis.includes(analysisKey) || analysisKey.includes(keyAnalysis)) {
        if (keyAnalysis.length > bestKeyLen) {
          best = v;
          bestKeyLen = keyAnalysis.length;
        }
      }
    }
    return best ?? [];
  };

  const toggleExpanded = (rowKey: string) => {
    setExpandedLiveAnalysisRowIds((prev) =>
      prev.includes(rowKey) ? prev.filter((x) => x !== rowKey) : [...prev, rowKey]
    );
  };

  const openRunSnapshotSlide = (subject: RunSnapshotSlideSubject) => setRunSnapshotSubject(subject);
  const closeRunSnapshotSlide = () => setRunSnapshotSubject(null);

  return (
    <>
      <main className="flex-1 flex flex-col min-h-0 min-w-0">
        <div className={`flex-1 overflow-auto pb-8 pt-3 sm:pt-4 ${shellMainPaddingX}`}>
          <div className="w-full space-y-6 relative">
            {error ? (
              <div
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                role="alert"
              >
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => refreshCompanies()}
                  className="shrink-0 px-3 py-1.5 rounded-md border border-red-300 text-red-900 text-xs font-medium hover:bg-red-100"
                >
                  Retry
                </button>
              </div>
            ) : null}

            <div className="flex items-start justify-between gap-4 flex-wrap">
              <p className="text-sm text-gray-500 max-w-2xl">
                Live and legacy 280G activity across all companies. Totals and the live analysis list are loaded per
                company.
              </p>
              {showCompaniesTable ? (
                <button
                  type="button"
                  onClick={() => loadWorkspaceFromApi()}
                  disabled={workspaceLoading}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
                >
                  {workspaceLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                  )}
                  Refresh workspace data
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {summaryBadges.map(({ label, value, loading: ld, tone, helper }) => (
                <div
                  key={label}
                  className={[
                    'rounded-xl border border-slate-200/80 border-t-2 bg-white px-4 py-3.5 shadow-sm flex flex-col gap-2',
                    tone === 'emerald'
                      ? 'border-t-emerald-300'
                      : tone === 'indigo'
                        ? 'border-t-indigo-300'
                        : tone === 'amber'
                          ? 'border-t-amber-300'
                          : 'border-t-slate-300',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500 leading-5">{label}</span>
                    {label === 'Companies' ? (
                      <Building2
                        className="h-4 w-4 text-slate-600 bg-slate-100 rounded-md p-0.5 box-content"
                        aria-hidden
                      />
                    ) : label.startsWith('Live') ? (
                      <ClipboardList
                        className="h-4 w-4 text-emerald-700 bg-emerald-100 rounded-md p-0.5 box-content"
                        aria-hidden
                      />
                    ) : label.startsWith('Legacy analysis') ? (
                      <History
                        className="h-4 w-4 text-indigo-700 bg-indigo-100 rounded-md p-0.5 box-content"
                        aria-hidden
                      />
                    ) : (
                      <AlertTriangle
                        className="h-4 w-4 text-amber-700 bg-amber-100 rounded-md p-0.5 box-content"
                        aria-hidden
                      />
                    )}
                  </div>
                  <span className="text-2xl font-medium text-gray-900 tabular-nums leading-tight">
                    {ld ? <span className="text-gray-400">...</span> : value}
                  </span>
                  <span className="text-xs text-gray-500">{helper}</span>
                </div>
              ))}
            </div>

            {partialWorkspaceFailure && workspaceReady ? (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 flex flex-wrap items-center gap-2"
                role="status"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                <span>Some companies failed to load. Totals include only successful responses.</span>
              </div>
            ) : null}

            {workspaceError ? (
              <div
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                role="alert"
              >
                <span>{workspaceError}</span>
                <button
                  type="button"
                  onClick={() => loadWorkspaceFromApi()}
                  className="shrink-0 px-3 py-1.5 rounded-md border border-red-300 text-red-900 text-xs font-medium hover:bg-red-100"
                >
                  Retry
                </button>
              </div>
            ) : null}

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-medium text-gray-800">Live Workspace Analyses</h2>
                {workspaceLoading && rows.length > 0 ? (
                  <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    Loading…
                  </span>
                ) : null}
              </div>
              <div className={clientDataPanelClass}>
                <table className="w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-gray-200 bg-gray-50 shadow-[0_1px_0_0_rgb(229_231_235)]">
                      <th className="min-w-[180px] px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Analysis
                      </th>
                      <th className="min-w-[120px] px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Company
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 tabular-nums">
                        Individuals
                      </th>
                      <th className="min-w-[150px] px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="w-28 px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 tabular-nums">
                        Runs
                      </th>
                      <th className="w-44 px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Last run date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {companiesLoading && rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-500">
                          Loading companies…
                        </td>
                      </tr>
                    ) : !companiesLoading && !error && rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-500">
                          No companies yet. Use <span className="font-medium text-gray-700">Companies</span> in the
                          sidebar to add one.
                        </td>
                      </tr>
                    ) : workspaceLoading && rows.length > 0 && liveAnalyses.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-500">
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden />
                            Loading workspace analyses…
                          </span>
                        </td>
                      </tr>
                    ) : !workspaceLoading && liveAnalyses.length === 0 && rows.length > 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-500">
                          No live workspace analyses yet. Client users create analyses in the client portal; legacy
                          completed runs appear in the totals above and on each company&apos;s detail page.
                        </td>
                      </tr>
                    ) : (
                      liveAnalyses.map((a) => {
                        const analysisKey = String(a.name ?? '')
                          .trim()
                          .toLowerCase();
                        const rowKey = `${a.companyId}-${a.id}`;
                        const legacyRunsForRow = getLegacyRunsForAnalysisKey(a.companyId, analysisKey);
                        const canExpand = legacyRunsForRow.length > 0;
                        const isExpanded = expandedLiveAnalysisRowIds.includes(rowKey);
                        const statusLabel = String(a.status ?? '').trim() || 'Not started';
                        const lastRunDateRaw = a.lastRunAt ?? legacyRunsForRow[0]?.runDate ?? null;
                        const lastRunDateDisplay =
                          lastRunDateRaw && !Number.isNaN(new Date(lastRunDateRaw).getTime())
                            ? formatWorkspaceDate(lastRunDateRaw)
                            : (lastRunDateRaw ?? '—');
                        const updatedLabel = formatFirmAnalysisUpdatedSubtitle(a.updatedAt);

                        return (
                          <React.Fragment key={rowKey}>
                            <tr className="group transition-colors hover:bg-gray-50/90">
                              <td className="align-top px-5 py-4">
                                <div className="flex items-start gap-3">
                                  <FirmLiveAnalysisExpandChevron
                                    canExpand={canExpand}
                                    isExpanded={isExpanded}
                                    ariaControlsId={`dashboard-run-history-${rowKey}`}
                                    onToggle={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (!canExpand) return;
                                      toggleExpanded(rowKey);
                                    }}
                                  />

                                  <div className="min-w-0">
                                    <div className="inline-flex min-w-0 items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => navigate(`/firm/companies/${a.companyId}`)}
                                        className="min-w-0 truncate rounded-sm text-left font-medium text-emerald-700 hover:text-emerald-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600/50"
                                        title="Open company details"
                                      >
                                        {a.name || 'Untitled analysis'}
                                      </button>
                                      <FirmAnalysisPersistedLockMark
                                        analysisPersistedStatus={a.analysisPersistedStatus}
                                      />
                                    </div>
                                    {updatedLabel ? (
                                      <p className="mt-1 text-[11px] leading-snug text-gray-400">
                                        Updated {updatedLabel}
                                      </p>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                              <td className="align-top px-5 py-4 text-sm text-gray-800">{a.companyName}</td>
                              <td className="align-top px-5 py-4 text-right text-sm tabular-nums text-gray-700">
                                {a.individualsCount}
                              </td>
                              <td className="align-top px-5 py-4">
                                <FirmLiveAnalysisWorkflowStatusBadge
                                  workflowProgress={a.workflowProgress}
                                  statusLabel={statusLabel}
                                />
                              </td>
                              <td className="align-top px-5 py-4 text-right text-sm tabular-nums text-gray-700">
                                {legacyRunsForRow.length}
                              </td>
                              <td className="align-top whitespace-nowrap px-5 py-4 text-right text-sm text-gray-700">
                                {lastRunDateDisplay}
                              </td>
                            </tr>
                            {isExpanded ? (
                              <tr
                                className="bg-gray-50/60 cursor-default"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <td colSpan={6} className="px-5 py-3 cursor-default">
                                  <FirmLiveAnalysisRunHistoryPanel
                                    panelId={`dashboard-run-history-${rowKey}`}
                                    title="Legacy run history"
                                    legacyRuns={legacyRunsForRow}
                                    className="cursor-default"
                                    onViewSnapshot={(r) => openRunSnapshotSlide(runHistoryRowToSnapshotSlideSubject(r))}
                                    emptyHint="No legacy run history."
                                  />
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-4 py-3 text-sm text-gray-600">
              <p>
                <span className="font-medium text-gray-700">Legacy analysis runs</span> are historical 280G batches from
                the legacy system (status and over-threshold individuals per run). Open a company for full history and
                client workspace details.
              </p>
            </section>
          </div>
        </div>
      </main>

      <RunSnapshotSlideOver subject={runSnapshotSubject} onClose={closeRunSnapshotSlide} />
    </>
  );
}
